import { Check, ChevronRight, Copy, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusCodeBadge, TONE_BAR_CLASS } from '@/components/atoms/StatusCodeBadge';
import { RetrievedChunkList } from '@/components/molecules/RetrievedChunkList';
import { dedupeChunks } from '@/components/molecules/RetrievedChunkList/RetrievedChunkList.utils';
import { useUsd } from '@/hooks/useUsd';
import { copyToClipboard } from '@/utils/clipboard';
import { formatDuration, formatEventTime } from '@/utils/datetime';
import { decamelizeKeysDeep } from '@/utils/decamelize';
import { deriveToolStatus } from '@/utils/toolStatus';

import type { ToolCallCardProps } from './ToolCallCard.types';

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
	<div className="flex flex-col gap-3xs">
		<p className="font-sans text-[10px] font-medium uppercase tracking-wider text-subtle">{label}</p>
		{children}
	</div>
);

const Json = ({ value }: { value: unknown }) => (
	<pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-white px-xs py-xs text-[11px] leading-relaxed text-default">
		{JSON.stringify(value, null, 2)}
	</pre>
);

const CopyJsonButton = ({ payload }: { payload: unknown }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const [copied, setCopied] = useState(false);

	const handleClick = async () => {
		if (!(await copyToClipboard(JSON.stringify(payload, null, 2)))) return;
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1500);
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			aria-live="polite"
			className={`inline-flex items-center gap-3xs self-start rounded-md border border-subtle bg-white px-xs py-3xs font-sans text-[11px] transition-colors hover:bg-grey-25 ${
				copied ? 'text-success' : 'text-subtle'
			}`}
		>
			{copied ? <Check className="h-sm w-sm" /> : <Copy className="h-sm w-sm" />}
			{copied ? t('COPIED_JSON') : t('COPY_JSON')}
		</button>
	);
};

/**
 * A tool call is not something anybody said, so it is not a bubble: it is a
 * full-width technical block sitting in its chronological place. The 3px left
 * rail in the status colour is what lets an operator scan the margin of a
 * 14-turn thread and find where it broke.
 */
export const ToolCallCard = ({
	tool,
	messageId,
	createdAt,
	model,
	relatedEvent,
	childEvents,
	highlighted,
	canNavigateToKnowledge = false,
}: ToolCallCardProps) => {
	const usd = useUsd();
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const { t: t2 } = useTranslation('translation', { keyPrefix: 'toolCall' });
	const status = deriveToolStatus(tool);
	// Failures are what gets read. Everything else stays folded, or a thread
	// with four tool calls becomes a wall of JSON.
	// A turn pointed at from the URL opens itself: somebody who arrived with a
	// specific id should not have to hunt for it and expand it by hand.
	const [open, setOpen] = useState(status.tone === 'error' || !!highlighted);
	// Changing `?trace=` does not remount the card — the key is stable — so the
	// useState initializer never reruns. Without this, moving from one trace to
	// another highlights the new card but leaves it closed.

	useEffect(() => {
		if (highlighted) setOpen(true);
	}, [highlighted]);
	const eventPayload = relatedEvent ? (decamelizeKeysDeep(relatedEvent.payload) as Record<string, unknown>) : null;
	// Evidence comes from the event itself or from its children: on the
	// supervisor arm the chunks are retrieved by each `kb_domain_search`, not by
	// the parent `kb_search`.
	const retrieved = dedupeChunks([
		...(relatedEvent?.payload?.retrieved ?? []),
		...(childEvents ?? []).flatMap((child) => child.payload?.retrieved ?? []),
	]);

	return (
		<li className="flex" data-testid={`tool-call-${tool.id}`} data-highlighted={highlighted ? 'true' : undefined}>
			<div
				className={`flex w-full overflow-hidden rounded-md border bg-grey-25 ${
					highlighted ? 'border-purple-500 ring-1 ring-purple-500' : 'border-subtle'
				}`}
			>
				<span className={`w-[3px] shrink-0 ${TONE_BAR_CLASS[status.tone]}`} aria-hidden />
				<div className="min-w-0 flex-1">
					<button
						type="button"
						onClick={() => setOpen((v) => !v)}
						aria-expanded={open}
						className="flex w-full items-center gap-2xs px-xs py-2xs text-left transition-colors hover:bg-white"
					>
						<ChevronRight
							className={`h-sm w-sm shrink-0 text-subtle transition-transform ${open ? 'rotate-90' : ''}`}
						/>
						<Wrench className="h-[12px] w-[12px] shrink-0 text-subtle" />
						<code className="min-w-0 flex-1 truncate font-mono text-[12px] font-semibold text-default">
							{tool.name}
						</code>
						{relatedEvent?.durationMs != null && (
							<span className="shrink-0 font-mono text-[11px] leading-none text-subtle tabular-nums">
								{formatDuration(relatedEvent.durationMs)}
							</span>
						)}
						<StatusCodeBadge status={status} />
					</button>

					{open && (
						<div className="flex flex-col gap-md border-t border-subtle px-xs py-xs">
							<Row label={t('TOOL_INPUT')}>
								<Json value={tool.args} />
							</Row>

							{tool.error != null && (
								<Row label={t('TOOL_ERROR')}>
									<Json value={tool.error} />
								</Row>
							)}

							<Row label={t('TOOL_OUTPUT')}>
								{tool.output === null || tool.output === undefined ? (
									<p className="rounded-md bg-white px-xs py-xs font-sans text-[12px] text-subtle">
										{t('TOOL_NO_OUTPUT')}
									</p>
								) : (
									<Json value={tool.output} />
								)}
							</Row>

							<dl className="grid grid-cols-2 gap-x-md gap-y-2xs font-sans text-[11px] md:grid-cols-3">
								<Meta label={t('TOOL_CALL_ID')} value={tool.id} mono />
								<Meta label={t('TOOL_MESSAGE_ID')} value={messageId} mono />
								<Meta label={t('TOOL_TIMESTAMP')} value={formatEventTime(createdAt)} mono />
								<Meta
									label={t('TOOL_AUDIT_LATENCY')}
									value={tool.durationMs ? formatDuration(tool.durationMs) : '—'}
									title={tool.durationMs ? undefined : t('TOOL_TIMING_UNKNOWN')}
									mono
								/>
								<Meta
									label={t('TOOL_COST')}
									value={tool.costUsd ? usd.unit(tool.costUsd) : '—'}
									title={tool.costUsd ? undefined : t('TOOL_TIMING_UNKNOWN')}
									mono
								/>
								{model && <Meta label={t('TOOL_MODEL')} value={model} mono />}
							</dl>

							{retrieved.length > 0 && (
								<Row label={t2('EVIDENCE_TITLE')}>
									<RetrievedChunkList chunks={retrieved} canNavigate={canNavigateToKnowledge} />
								</Row>
							)}

							{eventPayload && (
								<Row label={t('TOOL_RELATED_EVENTS')}>
									<p className="mb-2xs font-sans text-[11px] leading-relaxed text-subtle">
										{t('TOOL_RELATED_EVENTS_HINT')}
									</p>
									<p className="mb-2xs font-sans text-[12px] text-default">{relatedEvent?.summary}</p>
									<Json value={eventPayload} />
								</Row>
							)}

							<CopyJsonButton payload={{ toolCall: tool, messageId, createdAt, relatedEvent: relatedEvent ?? null }} />
						</div>
					)}
				</div>
			</div>
		</li>
	);
};

const Meta = ({ label, value, mono, title }: { label: string; value: string; mono?: boolean; title?: string }) => (
	<div className="flex min-w-0 flex-col gap-3xs" title={title}>
		<dt className="text-[10px] font-medium uppercase tracking-wider text-subtle">{label}</dt>
		<dd className={`truncate text-[11px] text-default ${mono ? 'font-mono' : ''}`}>{value}</dd>
	</div>
);
